import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import { SearchQuery } from '../models/search-query.model';
import {
  RentalSalesFinderFilter,
  RentalSalesFacetOption,
  RENTAL_SALES_AIRCRAFT_MANUFACTURER_OPTIONS,
  RENTAL_SALES_AIRCRAFT_RENTAL_OPTIONS,
  RENTAL_SALES_AVIONICS_MANUFACTURER_OPTIONS,
  RENTAL_SALES_SALES_TYPE_OPTIONS
} from '../../shared/models/rental-sales-finder-filter.model';
import { RentalSalesFinderFiltersService } from '../../shared/services/rental-sales-finder-filters.service';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';

/**
 * Group keys on RentalSalesFinderFilter that hold an array of selected tag names.
 */
type FacetGroup = 'aircraftRental' | 'salesTypes' | 'aircraftSalesManufacturers' | 'avionicsSalesManufacturers';

/**
 * Identifier for a top-level accordion section's own expand/collapse + badge state. Usually just its
 * FacetGroup, except 'salesSection' (the "Sales" wrapper), which owns no checkboxes of its own anymore — its
 * facets all live in its subsections (Aircraft Sale Type / Aircraft Manufacturers / Avionics Manufacturers).
 */
type SectionKey = FacetGroup | 'salesSection';

/**
 * Filter UI for the Rentals/Sales Finder, mirroring {@link FboFinderFilterListComponent}: collapsible
 * accordion sections with a selected-count badge and a selected-filter chip row; the Sales section nests its
 * manufacturer lists as collapsible subsections. Reads and writes the shared
 * {@link RentalSalesFinderFiltersService} state and emits `filtersChange`.
 */
@Component({
  selector: 'app-rental-sales-finder-filter-list',
  templateUrl: './rental-sales-finder-filter-list.component.html',
  styleUrls: ['./rental-sales-finder-filter-list.component.scss']
})
export class RentalSalesFinderFilterListComponent implements OnInit, OnDestroy {
  @Input() compact: boolean;
  @Input() query: SearchQuery;
  @Output() filtersChange = new EventEmitter<void>();

  /**
   * The collapsible facet sections, in display order: the group key on RentalSalesFinderFilter, the header
   * label, and the checkbox options. All groups use OR logic (see filterRentalSales). The Sales section owns
   * no checkboxes directly — Aircraft Sale Type, Aircraft Manufacturers and Avionics Manufacturers all nest under
   * it as equal-level collapsible subsections.
   */
  readonly sections: {
    key: SectionKey;
    title: string;
    options: RentalSalesFacetOption[];
    subsections?: { key: FacetGroup; title: string; options: RentalSalesFacetOption[] }[];
  }[] = [
    { key: 'aircraftRental', title: 'Aircraft Rental', options: RENTAL_SALES_AIRCRAFT_RENTAL_OPTIONS },
    { key: 'salesSection', title: 'Sales', options: [], subsections: [
      { key: 'salesTypes', title: 'Aircraft Sale Type', options: RENTAL_SALES_SALES_TYPE_OPTIONS },
      { key: 'aircraftSalesManufacturers', title: 'Aircraft Manufacturers', options: RENTAL_SALES_AIRCRAFT_MANUFACTURER_OPTIONS },
      { key: 'avionicsSalesManufacturers', title: 'Avionics Manufacturers', options: RENTAL_SALES_AVIONICS_MANUFACTURER_OPTIONS }
    ] }
  ];

  /**
   * Per-section expand/collapse state. Sections start collapsed; any section that already has selected
   * filters is auto-expanded on first load so restored/active filters are visible. Each header shows a
   * count badge.
   */
  expanded: { [group: string]: boolean } = {
    aircraftRental: false,
    salesSection: false,
    salesTypes: false,
    aircraftSalesManufacturers: false,
    avionicsSalesManufacturers: false
  };
  private _expansionInitialized = false;

  /** Max number of selected-filter chips to render before collapsing the rest into a "+N more" marker. */
  readonly maxChips = 5;

  /** The chips actually shown (first {@link maxChips} selected filters, in section + option order). */
  visibleChips: { group: FacetGroup; value: string; label: string }[] = [];

  /** How many selected filters are hidden beyond {@link maxChips} (0 = none hidden). */
  overflowCount = 0;

  rentalSalesFinderFilter = new RentalSalesFinderFilter();
  private _subscription: Subscription;

  /**
   * True when a business name has been entered but is too short to count toward a search. Mirrors the
   * `rentalSalesKeyword.length >= 3` gate in {@link SearchQuery.validate}, so the hint shows exactly while
   * the entered name would be ignored.
   */
  get nameTooShort(): boolean {
    const trimmed = this.rentalSalesFinderFilter.name ? this.rentalSalesFinderFilter.name.trim() : '';
    return trimmed.length > 0 && trimmed.length < 3;
  }

  constructor(private _rentalSalesFinderFiltersService: RentalSalesFinderFiltersService,
              private _analyticsService: AnalyticsService) { }

  ngOnInit() {
    this._subscription = this._rentalSalesFinderFiltersService.rentalSalesFinderFilter.subscribe(f => {
      this.rentalSalesFinderFilter = f;
      this._recomputeChips();
      // Auto-expand sections with active filters once (on first load); don't fight the user's manual toggles
      // on subsequent emissions (each checkbox edit re-emits the same filter).
      if (!this._expansionInitialized) {
        this._expansionInitialized = true;
        this.sections.forEach(s => {
          const subsections = s.subsections || [];
          const subHas = subsections.map(sub => !!(f && f[sub.key] && f[sub.key].length));
          // Sections with no options of their own (e.g. the 'salesSection' wrapper) don't map to a real
          // FacetGroup, so only look at f[s.key] when the section actually owns checkboxes.
          const ownHas = s.options.length > 0 && !!(f && f[s.key as FacetGroup] && f[s.key as FacetGroup].length);
          this.expanded[s.key] = ownHas || subHas.some(h => h);  // parent opens if anything inside is selected
          subsections.forEach((sub, i) => this.expanded[sub.key] = subHas[i]);  // a subsection opens only if it has selections
        });
      }
    });
    this._analyticsService.pageView('airport_landingpage_rentalsales', 'Rentals/Sales Finder Landing Page filters');
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  isFacetSelected(group: FacetGroup, value: string): boolean {
    return this.rentalSalesFinderFilter[group].indexOf(value) !== -1;
  }

  onToggleFacet(group: FacetGroup, value: string, checked: boolean) {
    const selected = this.rentalSalesFinderFilter[group];
    const i = selected.indexOf(value);
    if (checked && i === -1) {
      selected.push(value);
    } else if (!checked && i !== -1) {
      selected.splice(i, 1);
    }
    this._emit();
  }

  onSelectAll(group: FacetGroup, options: RentalSalesFacetOption[]) {
    this.rentalSalesFinderFilter[group] = options.map(o => o.value);
    this._emit();
  }

  onClearAll(group: FacetGroup) {
    this.rentalSalesFinderFilter[group] = [];
    this._emit();
  }

  onNameChange() {
    this._emit();
  }

  toggleSection(group: SectionKey) {
    this.expanded[group] = !this.expanded[group];
  }

  /** Number of selected filters in a single group (shown as the count badge on that group's header). */
  selectedCount(group: FacetGroup): number {
    const selected = this.rentalSalesFinderFilter[group];
    return selected ? selected.length : 0;
  }

  /**
   * Total selected filters for a parent section — its own options (if any) plus any nested subsections —
   * so the parent badge still reflects subsection selections while the subsection is collapsed.
   */
  sectionBadgeCount(section: { key: SectionKey; options: RentalSalesFacetOption[]; subsections?: { key: FacetGroup }[] }): number {
    let count = section.options.length ? this.selectedCount(section.key as FacetGroup) : 0;
    (section.subsections || []).forEach(sub => count += this.selectedCount(sub.key));
    return count;
  }

  trackByChip(index: number, chip: { value: string }): string {
    return chip.value;
  }

  /**
   * Rebuilds the selected-filter chip list from the current filter state (called on every filter change).
   * Chips are collected in section + option order; only the first {@link maxChips} are shown, the rest are
   * summarized by {@link overflowCount}.
   */
  private _recomputeChips() {
    const chips: { group: FacetGroup; value: string; label: string }[] = [];
    const collect = (key: FacetGroup, options: RentalSalesFacetOption[]) => {
      const selected = this.rentalSalesFinderFilter[key] || [];
      options.forEach(option => {
        if (selected.indexOf(option.value) !== -1) {
          chips.push({ group: key, value: option.value, label: option.label });
        }
      });
    };
    this.sections.forEach(section => {
      // section.key is 'salesSection' for the options-less Sales wrapper; collect() is a no-op there since
      // section.options is empty, so the cast is safe.
      collect(section.key as FacetGroup, section.options);
      (section.subsections || []).forEach(sub => collect(sub.key, sub.options));
    });
    this.visibleChips = chips.slice(0, this.maxChips);
    this.overflowCount = Math.max(0, chips.length - this.maxChips);
  }

  private _emit() {
    // Keep the query's filter in sync as the user edits, so a business name entered here counts toward search
    // validation (see SearchQuery.validate) even before the query is serialized/submitted.
    if (this.query) {
      this.query.rentalSalesFinderFilter = this.rentalSalesFinderFilter;
    }
    this._rentalSalesFinderFiltersService.updateRentalSalesFinderFilters(this.rentalSalesFinderFilter);
    this.filtersChange.emit();
  }
}
